import classNames from 'classnames'
import React, { useState, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react'
import { twMerge } from 'tailwind-merge'

interface MultiEmailInputProps {
  value: string[]
  onChange?: (emails: string[]) => void
  isEditing: boolean
  placeholder?: string
  isError?: boolean
  className?: string
}

const MultiEmailInput: React.FC<MultiEmailInputProps> = ({
  value,
  onChange,
  isEditing,
  placeholder,
  isError = false,
  className,
}) => {
  const [inputValue, setInputValue] = useState<string>('')

  const validateEmail = (email: string): boolean => /\S+@\S+\.\S+/.test(email)

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',' || e.key === 'Tab') && inputValue.trim()) {
      e.preventDefault()
      addEmail(inputValue.trim())
    }
  }

  const addEmail = (email: string) => {
    if (onChange && validateEmail(email) && !value.includes(email)) {
      onChange([...value, email])
      setInputValue('')
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const newEmails = pastedText
      .split(/[\s,;]+/)
      .map((email) => email.trim())
      .filter((email) => validateEmail(email) && !value.includes(email)) // ✅ prevent duplicates

    if (onChange && newEmails.length) {
      onChange([...value, ...newEmails])
    }
  }

  const removeEmail = (index: number) => {
    if (onChange) {
      onChange(value.filter((_, i) => i !== index))
    }
  }

  const inputClasses = classNames(
    twMerge(
      `relative text-neutral-2 p-3 text-base 
          ${!isError && 'border-neutral-6'} 
          ${className}`,
    ),
    `focus:border-current focus:ring-0 relative inline-flex appearance-none border-2 font-bold placeholder-neutral-5 placeholder:font-medium focus:placeholder-neutral-5 transition-colors ease-in-out rounded-xl h-[52px] px-4 leading-tight focus:outline-none focus:ring-neutral-5 focus:shadow-outline`,
    className,
  )

  return (
    <div className={inputClasses}>
      {value.length === 0 && !isEditing && <span style={{ color: '#888' }}>No emails</span>}
      {value.map((email, index) => (
        <span className="flex items-center bg-gray-200 px-2 rounded-lg min-h-[40px]" key={index}>
          {email}
          {isEditing && (
            <button
              className="ml-2 border-none bg-transparent cursor-pointer"
              type="button"
              onClick={() => removeEmail(index)}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {isEditing && (
        <input
          className="w-full"
          type="text"
          value={inputValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder || 'Type email here and press Enter'}
        />
      )}
    </div>
  )
}

export default MultiEmailInput
